import React from 'react';
import { Users, Award, Globe2, Lightbulb, Code2, Rocket, CheckCircle2, Quote, BarChart, Target, Shield, ArrowRight } from 'lucide-react';

const About = () => {
  const stats = [
    { number: '25+', label: 'Years Experience' },
    { number: '200+', label: 'Projects Delivered' },
    { number: '50+', label: 'Enterprise Clients' },
    { number: '100%', label: 'Client Satisfaction' }
  ];

  const values = [
    {
      icon: <Code2 className="w-8 h-8 text-yellow-400" />,
      title: 'Technical Excellence',
      description: 'We maintain the highest standards of code quality and architectural design.'
    },
    {
      icon: <Users className="w-8 h-8 text-yellow-400" />,
      title: 'Client Partnership',
      description: 'We build lasting relationships through collaboration and shared success.'
    },
    {
      icon: <Lightbulb className="w-8 h-8 text-yellow-400" />,
      title: 'Innovation First',
      description: 'We stay ahead of technology trends to deliver cutting-edge solutions.'
    },
    {
      icon: <Globe2 className="w-8 h-8 text-yellow-400" />,
      title: 'Global Impact',
      description: 'Our solutions drive digital transformation across industries worldwide.'
    }
  ];

  const testimonials = [
    {
      quote: "Stinger Builds transformed our legacy systems into a modern, efficient platform. Their technical expertise and project management were exceptional.",
      author: "David Chen",
      position: "CTO",
      company: "TechFlow Solutions",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=300&q=80"
    },
    {
      quote: "The team's attention to detail and commitment to quality helped us launch our SaaS platform ahead of schedule. They're truly partners in our success.",
      author: "Sarah Martinez",
      position: "CEO",
      company: "InnovateHub",
      image: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=300&q=80"
    },
    {
      quote: "Working with Stinger Builds has been transformative for our business. Their solutions have helped us achieve 200% growth in just 18 months.",
      author: "Michael Thompson",
      position: "Director of Operations",
      company: "Global Systems Inc",
      image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=300&q=80"
    }
  ];

  const impactMetrics = [
    {
      icon: <BarChart className="w-8 h-8 text-yellow-400" />,
      title: "Business Growth",
      metric: "150%",
      description: "Average revenue increase for our clients after implementing our solutions"
    },
    {
      icon: <Target className="w-8 h-8 text-yellow-400" />,
      title: "Operational Efficiency",
      metric: "65%",
      description: "Reduction in operational costs through our automation solutions"
    },
    {
      icon: <Shield className="w-8 h-8 text-yellow-400" />,
      title: "System Reliability",
      metric: "99.99%",
      description: "Uptime achieved for mission-critical applications"
    }
  ];

  const qualities = [
    "Commitment to Quality",
    "Proactive problem-solving",
    "Diverse project expertise",
    "Client-focused solutions",
    "Strong trade experience",
    "Cutting-edge technology integration"
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800" />
        <div className="relative container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-5xl font-bold mb-6">Pioneering Software Excellence</h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              We're a team of passionate technologists dedicated to transforming businesses 
              through innovative software solutions. Our mission is to empower organizations 
              with cutting-edge technology that drives growth and success.
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                Precision Software.<br />
                Real Solutions.
              </h2>
              <h3 className="text-2xl text-gray-700 mb-6">Who we are at Stinger Builds, LLC</h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                With over 25 years of industry experience, Stinger Builds LLC excels in a diverse 
                range of technologies, including bespoke software development, SaaS hosting, 
                legacy software implementation, and distressed projects. Our customer-first 
                approach and exceptional project management skills ensure successful execution, 
                making us a trusted partner in delivering high-quality custom software and 
                technology solutions.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {qualities.map((quality, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <CheckCircle2 className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                    <span className="text-gray-700">{quality}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://stingerbuilds.com/Precision-Software.-Real-Solutions.-visual-selection.png"
                alt="Precision Software. Real Solutions."
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">Our Core Values</h2>
            <p className="text-lg text-gray-600">
              These principles guide everything we do and shape how we deliver value to our clients.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="mb-6">{value.icon}</div>
                <h3 className="text-xl font-semibold mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Award className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Our Impact</h2>
            <p className="text-lg text-gray-600">
              We don't just build software—we drive transformative business outcomes
              that create lasting value for our clients.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            {impactMetrics.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="bg-slate-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-all">
                  <div className="inline-block p-4 bg-white rounded-full shadow-md mb-6">
                    {metric.icon}
                  </div>
                  <div className="text-4xl font-bold text-yellow-400 mb-4">{metric.metric}</div>
                  <h3 className="text-xl font-semibold mb-4">{metric.title}</h3>
                  <p className="text-gray-600">{metric.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-16 text-center">
            <a
              href="/case-studies"
              className="inline-flex items-center px-8 py-4 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-all group"
            >
              View Success Stories
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Quote className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Client Success Stories</h2>
            <p className="text-lg text-gray-600">
              Don't just take our word for it. Here's what our clients have to say about working with us.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="mb-6">
                  <Quote className="w-8 h-8 text-yellow-400" />
                </div>
                <blockquote className="text-gray-600 mb-6 italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.author}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.author}</div>
                    <div className="text-sm text-gray-500">{testimonial.position}</div>
                    <div className="text-sm text-yellow-400">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Transform Your Business?</h2>
            <p className="text-xl text-gray-300 mb-8">
              Join the ranks of industry leaders who trust us with their digital transformation journey.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-4 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all group"
            >
              Start Your Project
              <Rocket className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;